import { Form } from 'formik';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'react-use';
import classNames from 'classnames';
import useT from '../../utils/Translations/useT';
import EntityFormPrompt from './EntityFormPrompt';
import EntityFormContent from './internal/EntityFormContent';
import SubmitSection from './internal/SubmitSection';
import RoundedPrimaryButton from '../../controls/RoundedPrimaryButton';
import Spinner from '../../utils/Spinner';
import styles from './EntityFormBody.scss';
import { useIsOnline } from '../../utils/InternetConnectionProvider';
import useEntityFormContext from './internal/useEntityFormContext';
const DEBOUNCE_TIME = 300;
const SCROLL_OFFSET = 150;
const RESIZE_DEBOUNCE_DELAY = 10;
export default function EntityFormBody({ leftBottomSection, isSubmitTogglesDropdown, isSubmitDisabled, isCancelDisabled, hasClearFormButton, goBackLabel, defaultEntity, submitActions, submitSectionRenderer, isNew, promptMessage, hasLeavePrompt, updateLabel, deleteLabel, children, onDelete, onGoBack, hasResetOnCancel, onCancel, resetForm, dirty, values, isSubmitting, entity, columns, entityName, createLabel, cancelLabel, fieldWrapperClassNames, isEditing, hasErrors, onLeave, hasSubmitOnChange, pullLeft, submitContainerClass, submitClass, isValid, submitForm, isCopied, floatButton, submitBtnAdditionClasses, }) {
    const t = useT();
    const history = useHistory();
    const formRef = useRef(null);
    const resetOnCancel = useCallback(() => {
        hasResetOnCancel && resetForm();
        onCancel && onCancel();
    }, [onCancel, hasResetOnCancel, resetForm]);
    const _isDirty = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        const { gptVersion, replicateVersion, inputAuto, ..._values } = values;
        return dirty && !isEqual(_values, entity);
    }, [values, dirty, entity]);
    const _promptActive = useMemo(() => _isDirty && !isSubmitting, [
        _isDirty,
        isSubmitting,
    ]);
    const _promptMessage = useMemo(() => promptMessage ||
        t('The changes have not been saved yet, you need to click on the #{button} button in order to save changes', { button: isNew ? createLabel : updateLabel }), [t, promptMessage, isNew, createLabel, updateLabel]);
    useEffect(() => {
        if (!onLeave || !isEditing) {
            return undefined;
        }
        const release = history.block((location) => {
            (async () => {
                await onLeave(values, { isDirty: _isDirty, location, isValid });
            })();
            release();
            history.push(location);
        });
        return () => {
            release();
        };
    }, [_isDirty, isEditing, onLeave, history, values, isValid]);
    useDebounce(async () => {
        const valuesEqualInitialValues = isEqual(values, entity);
        if (hasSubmitOnChange &&
            !valuesEqualInitialValues &&
            isValid &&
            _isDirty) {
            await submitForm();
        }
    }, DEBOUNCE_TIME, [hasSubmitOnChange, values, entity, isValid, _isDirty, submitForm]);
    // eslint-disable-next-line no-nested-ternary
    const label = isSubmitting ? <Spinner /> : isNew ? createLabel : updateLabel;
    const { isOnline } = useIsOnline();
    const formikBag = useEntityFormContext();
    const _isSubmitDisabled = useMemo(() => {
        if (!isOnline) {
            return true;
        }
        const basicIsDisabled = isSubmitting || (!isNew && !_isDirty);
        return isSubmitDisabled
            ? isSubmitDisabled(isSubmitting, { ...formikBag, dirty: _isDirty })
            : basicIsDisabled;
    }, [isNew, isOnline, isSubmitting, formikBag, isSubmitDisabled, _isDirty]);
    const elementRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [isSubmitSectionReady, setIsSubmitSectionReady] = useState(false);
    useEffect(() => {
        if (!isSubmitSectionReady) {
            return;
        }
        const scrollElement = document.getElementById('scroll-layout');
        const handleScroll = () => {
            if (scrollElement) {
                const scrollBottom = scrollElement.scrollHeight -
                    scrollElement.scrollTop -
                    scrollElement.clientHeight -
                    SCROLL_OFFSET;
                setScrollTop(scrollBottom < 0 ? 0 : scrollBottom);
            }
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        let resizeObserver = null;
        if (scrollElement && formRef.current) {
            scrollElement.addEventListener('scroll', handleScroll);
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            resizeObserver = new ResizeObserver(() => {
                setTimeout(handleScroll, RESIZE_DEBOUNCE_DELAY);
            });
            resizeObserver.observe(formRef.current);
        }
        handleScroll();
        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', handleScroll);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isSubmitSectionReady]);
    const [isVisible, setIsVisible] = useState(true);
    useEffect(() => {
        const scrollElement = document.getElementById('scroll-layout');
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, {
            root: scrollElement,
            rootMargin: '0px 0px 120px 0px',
            threshold: 0,
        });
        const currentElement = elementRef.current;
        if (currentElement) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
            // @ts-ignore
            observer.observe(currentElement);
            setIsSubmitSectionReady(true);
        }
        return () => {
            if (currentElement) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                // @ts-ignore
                observer.unobserve(currentElement);
            }
        };
    }, []);
    const submitSection = useMemo(() => submitSectionRenderer ? (submitSectionRenderer({
        isEditing,
        isNew,
        onDelete,
        onGoBack,
        isDirty: _isDirty,
        isSubmitting,
        resetForm,
        hasErrors,
    })) : (<>
          <div>
            <SubmitSection actions={submitActions} cancelLabel={cancelLabel} createLabel={createLabel} defaultEntity={defaultEntity} deleteLabel={deleteLabel} elementRef={elementRef} goBackLabel={goBackLabel} hasClearFormButton={hasClearFormButton} hasErrors={hasErrors} isCancelDisabled={isCancelDisabled} isDirty={isCopied !== undefined ? isCopied : _isDirty} isEditing={isEditing} isNew={isNew} isSubmitDisabled={isSubmitDisabled} isSubmitting={isSubmitting} isSubmitTogglesDropdown={isSubmitTogglesDropdown} leftBottomSection={leftBottomSection} pullLeft={pullLeft} submitBtnAdditionClasses={submitBtnAdditionClasses} submitClass={submitClass} submitContainerClass={submitContainerClass} updateLabel={updateLabel} onCancel={onCancel || hasResetOnCancel ? resetOnCancel : undefined} onDelete={onDelete} onGoBack={onGoBack}/>
          </div>
          {floatButton && (<RoundedPrimaryButton additionClasses={classNames('btn btn-primary legitRipple', 
    // Show floating button only when the submit section is outside the scroll viewport
    floatButton &&
        isSubmitSectionReady &&
        !isVisible &&
        !isNew &&
        isEditing
        ? styles.stickyButton
        : styles.hiddenButton)} disabled={_isSubmitDisabled || !_isDirty} id="button-submit" 
    // eslint-disable-next-line react/forbid-component-props
    style={{
        bottom: `${scrollTop}px`,
    }} type="submit">
              {label}
            </RoundedPrimaryButton>)}
        </>), [
        deleteLabel,
        scrollTop,
        updateLabel,
        submitSectionRenderer,
        submitActions,
        resetOnCancel,
        resetForm,
        onDelete,
        onGoBack,
        onCancel,
        isSubmitting,
        leftBottomSection,
        isSubmitTogglesDropdown,
        isSubmitDisabled,
        isEditing,
        isNew,
        hasResetOnCancel,
        hasClearFormButton,
        goBackLabel,
        defaultEntity,
        createLabel,
        cancelLabel,
        _isDirty,
        hasErrors,
        floatButton,
        _isSubmitDisabled,
        label,
        isCancelDisabled,
        isCopied,
        pullLeft,
        submitContainerClass,
        submitClass,
        isVisible,
        isSubmitSectionReady,
        submitBtnAdditionClasses,
    ]);
    return (<EntityFormContent columns={columns} createLabel={createLabel} entityName={entityName} fieldWrapperClassNames={fieldWrapperClassNames} isEditing={isEditing} isNew={isNew} updateLabel={updateLabel}>
      <Form ref={formRef} className={classNames('clearfix', {
        [styles.floatButtonForm]: floatButton,
        [styles.overflowYAuto]: floatButton && !isNew,
    })} method="POST">
        {hasLeavePrompt ? (<EntityFormPrompt message={_promptMessage} when={_promptActive}/>) : null}

        {children}
        {submitSection}
      </Form>
    </EntityFormContent>);
}
